// src/Components/MouseControl/MouseControlView.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
    Box,
    Card,
    CardContent,
    Typography,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    TableContainer,
    Paper,
    useTheme,
    Stack,
    Chip
} from "@mui/material";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { LineChart } from "@mui/x-charts";
import { MouseActivityService } from "../../Services/mouseActivity.service.js";

// inject gradient defs
function AreaGradient({ id, color }) {
    return (
        <defs>
            <linearGradient id={id} x1="50%" y1="0%" x2="50%" y2="100%">
                <stop offset="0%" stopColor={color} stopOpacity={0.5} />
                <stop offset="100%" stopColor={color} stopOpacity={0} />
            </linearGradient>
        </defs>
    );
}

// Helper to format minutes as hh:mm
const fmtMins = (m) => (m > 0 ? `${Math.floor(m / 60)}h ${m % 60}m` : "-");

export default function MouseControlView({
    accountantId,
    filters,
    token,
    direction,
    subAccountants
}) {
    const { t } = useTranslation();
    const theme = useTheme();
    const [records, setRecords] = useState([]);

    // 1️⃣ fetch raw records on filter change
    useEffect(() => {
        (async () => {
            try {
                const payload = await MouseActivityService.filter({
                    AccountantId: accountantId,
                    UserIds: filters.subAccountantIds?.map(s => s.value) || [],
                    CaseId: filters.clientCaseIds?.map(c => c.value)?.[0] || null,
                    StartUtc: filters.startUtc.toISOString(),
                    EndUtc: filters.endUtc.toISOString(),
                    token
                });
                setRecords(payload.success ? payload.data : []);
            } catch {
                setRecords([]);
            }
        })();
    }, [filters, token]);

    // 2️⃣ build map monthKey→userId→day→totalMinutes
    const grouped = useMemo(() => {
        const map = {};
        records.forEach(r => {
            const mKey = `${r.year}-${String(r.month).padStart(2, "0")}`;
            map[mKey] ??= {};
            map[mKey][r.userId] ??= {};
            map[mKey][r.userId][r.day] =
                (map[mKey][r.userId][r.day] || 0) + r.activeMinutes;
        });
        return map;
    }, [records]);

    // 3️⃣ list every day in the filter range
    const daysInRange = useMemo(() => {
        const days = [];
        let cur = dayjs(filters.startUtc).startOf("day");
        const end = dayjs(filters.endUtc).endOf("day");
        while (cur.isBefore(end) || cur.isSame(end, "day")) {
            days.push(cur.toDate());
            cur = cur.add(1, "day");
        }
        return days;
    }, [filters]);

    // 4️⃣ compute daily totals (minutes → hours)
    const dailyTotalsH = useMemo(() => {
        return daysInRange.map(date => {
            const mKey = dayjs(date).format("YYYY-MM");
            const day = date.getDate();
            const users = grouped[mKey] || {};
            const totMin = Object.values(users)
                .reduce((sum, daysMap) => sum + (daysMap[day] || 0), 0);
            return +(totMin / 60).toFixed(2);
        });
    }, [daysInRange, grouped]);

    const firstH = dailyTotalsH[0] || 0;
    const lastH = dailyTotalsH[dailyTotalsH.length - 1] || 0;
    const sumH = dailyTotalsH.reduce((acc, h) => acc + h, 0);
    const pctChange = firstH > 0
        ? Math.round(((lastH - firstH) / firstH) * 100)
        : 0;
    const pctLabel = `${lastH >= firstH ? "+" : ""}${pctChange}%`;
    const avgH = dailyTotalsH.length
        ? +(dailyTotalsH.reduce((a, b) => a + b, 0) / dailyTotalsH.length).toFixed(2)
        : 0;

    // xAxis for daily chart
    const xAxisDaily = {
        data: daysInRange,
        scaleType: "time",
        nice: true,
        label: t("Date"),
        valueFormatter: (val, ctx) =>
            ctx.location === "tick"
                ? dayjs(val).format("D/M")
                : dayjs(val).format("D/M/YYYY")
    };

    // 5️⃣ list month-keys in range
    const monthsInRange = useMemo(() => {
        const months = [];
        let cur = dayjs(filters.startUtc).startOf("month");
        const end = dayjs(filters.endUtc).startOf("month");
        while (cur.isBefore(end - 1) || cur.isSame(end - 1, "month")) {
            months.push(cur);
            cur = cur.add(1, "month");
        }
        return months;
    }, [filters]);

    return (
        <Box dir={direction} sx={{ px: 2, mt: 2 }}>
            {/* — Total Over Time with % & Avg — */}
            <Card variant="outlined" sx={{ mb: 4, height: 400 }}>
                <CardContent>
                    <Typography variant="subtitle2" gutterBottom>
                        {t("TotalActualWorkDoneInTheSystemInTheTimeRange")}
                    </Typography>
                    <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                        mb={1}
                    >
                        <Typography variant="h4">{fmtMins(Math.round(sumH * 60))}</Typography>
                        <Chip size="small" label={`${t("Avg")}: ${fmtMins(Math.round(avgH * 60))}`} />
                    </Stack>
                    <LineChart
                        xAxis={[
                            {
                                ...xAxisDaily,
                                // make sure the X axis has a title
                                label: t("Date"),
                                labelProps: { style: { fontSize: 12, fontWeight: 600 } }
                            }
                        ]}
                        yAxis={[
                            {
                                // Y‐axis title “Hours”
                                label: t("Hours"),
                                labelProps: { style: { fontSize: 12, fontWeight: 600 } },
                            }
                        ]}
                        series={[{
                            id: "dailyTotal",
                            label: t("Total"),
                            data: dailyTotalsH,
                            showMark: false,
                            area: true,
                            stroke: theme.palette.primary.main
                        }]}
                        height={280}
                        margin={{ left: 40, right: 40, top: 40, bottom: 40 }}
                        slotProps={{
                            legend: { position: { vertical: "top", horizontal: "right" } }
                        }}
                        sx={{
                            "& .MuiAreaElement-series-dailyTotal": {
                                fill: "url(#grad-daily)"
                            }
                        }}
                    >
                        <AreaGradient id="grad-daily" color={theme.palette.primary.main} />
                    </LineChart>
                </CardContent>
            </Card>

            {/* two-column grid of per-month detail cards */}
            <Box
                sx={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: 2
                }}
            >
                {monthsInRange.map(m => {
                    const key = m.format("YYYY-MM");
                    const daysInMonth = m.daysInMonth();
                    const dayNumbers = Array.from({ length: daysInMonth }, (_, i) => i + 1);
                    const users = grouped[key] || {};

                    // one line series per user
                    const lineSeries = Object.entries(users).map(([uid, days]) => ({
                        id: uid,
                        label: subAccountants.find(sa => sa.value === uid)?.label || uid,
                        data: dayNumbers.map(d => +((days[d] || 0) / 60).toFixed(2)),
                        showMark: false,
                        stroke: theme.palette.primary.main
                    }));

                    return (
                        <Card key={key} variant="outlined" sx={{ mb: 4, maxWidth: "800px" }}>
                            <CardContent>
                                <Typography variant="subtitle2" gutterBottom>
                                    {t("WorkInSystem")} – {m.format("MMMM YYYY")}
                                </Typography>

                                {/* daily‐hours linechart */}
                                <LineChart
                                    xAxis={[
                                        { data: dayNumbers, scaleType: "band", label: t("Day") }
                                    ]}
                                    yAxis={[{ label: t("Hours") }]}
                                    series={lineSeries}
                                    height={200}
                                    sx={{
                                        mb: 2,
                                        "& .MuiAreaElement-series": {
                                            fill: `url(#grad-${key})`
                                        }
                                    }}
                                >
                                    <defs>
                                        <linearGradient id={`grad-${key}`} x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="0%" stopColor={theme.palette.primary.main} stopOpacity={0.4} />
                                            <stop offset="100%" stopColor={theme.palette.primary.main} stopOpacity={0} />
                                        </linearGradient>
                                    </defs>
                                </LineChart>

                                {/* hours‐per‐day table */}
                                <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
                                    <Table stickyHeader size="small">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell sx={{ fontWeight: 600, minWidth: 120 }}>
                                                    {t("User")}
                                                </TableCell>
                                                {dayNumbers.map(d => (
                                                    <TableCell key={d} sx={{ textAlign: "center", minWidth: 40 }}>
                                                        {d}
                                                    </TableCell>
                                                ))}
                                                <TableCell
                                                    sx={{
                                                        textAlign: "center",
                                                        fontWeight: 600,
                                                        position: "sticky",
                                                        right: 0,
                                                        background: "#f3f3f3"
                                                    }}
                                                >
                                                    {t("Total") + " " + t("Hours")}
                                                </TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {Object.entries(users).map(([uid, days]) => {
                                                const rowMin = dayNumbers.reduce((s, d) => s + (days[d] || 0), 0);
                                                return (
                                                    <TableRow key={uid}>
                                                        <TableCell>
                                                            {
                                                                subAccountants.find(sa => sa.value === uid)?.label ||
                                                                uid
                                                            }
                                                        </TableCell>
                                                        {dayNumbers.map(d => (
                                                            <TableCell key={d} sx={{ textAlign: "center" }}>
                                                                {fmtMins(days[d] || 0)}
                                                            </TableCell>
                                                        ))}
                                                        <TableCell
                                                            sx={{
                                                                textAlign: "center",
                                                                fontWeight: 700,
                                                                position: "sticky",
                                                                right: 0,
                                                                background: "#f3f3f3"
                                                            }}
                                                        >
                                                            {fmtMins(rowMin)}
                                                        </TableCell>
                                                    </TableRow>
                                                );
                                            })}

                                            {/* footer sums */}
                                            <TableRow>
                                                <TableCell sx={{ fontWeight: 700, background: "#f3f3f3" }}>
                                                    {t("DailyTotal")}
                                                </TableCell>
                                                {dayNumbers.map(d => {
                                                    const sumMin = Object.values(users)
                                                        .reduce((s, daysMap) => s + (daysMap[d] || 0), 0);
                                                    return (
                                                        <TableCell
                                                            key={d}
                                                            sx={{
                                                                textAlign: "center",
                                                                fontWeight: 700,
                                                                background: "#f3f3f3"
                                                            }}
                                                        >
                                                            {fmtMins(sumMin)}
                                                        </TableCell>
                                                    );
                                                })}
                                                <TableCell
                                                    sx={{
                                                        textAlign: "center",
                                                        fontWeight: 700,
                                                        position: "sticky",
                                                        right: 0,
                                                        background: "#f3f3f3"
                                                    }}
                                                >
                                                    {fmtMins(Object.values(users)
                                                        .flatMap(daysMap => Object.values(daysMap))
                                                        .reduce((a, b) => a + b, 0))}
                                                </TableCell>
                                            </TableRow>
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </CardContent>
                        </Card>
                    );
                })}
            </Box>
        </Box>
    );
}
